import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../App";
import { getApiPrefix } from '../utils/apiHelper';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateInput } from "../components/ui/date-input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { 
  Plus, Trash2, Loader2, Users, Home, Pencil, Building, 
  UserCheck, UserX, MapPin, Calendar, Briefcase, HardHat,
  ArrowRightLeft, History, Euro, Filter, X, Globe, IdCard, Car, Search
} from "lucide-react";
import DataTable from "../components/ui/data-table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useTranslation } from "../contexts/I18nContext";
import { DipendenteFormDialog, EditMovimentoDialog } from "./AlloggiDialogs";

export default function AlloggiDipendentiPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [dipendenti, setDipendenti] = useState([]);
  const [alloggi, setAlloggi] = useState(null);
  const [movimenti, setMovimenti] = useState(null);
  const [costiDipendenti, setCostiDipendenti] = useState(null);
  const [appartamenti, setAppartamenti] = useState([]);
  const [centriCosto, setCentriCosto] = useState([]);
  const [activeTab, setActiveTab] = useState("alloggi");
  
  // Filtri Registro Movimenti
  const [filtroMovDipendente, setFiltroMovDipendente] = useState("");
  const [filtroMovAppartamento, setFiltroMovAppartamento] = useState("");
  const [filtroMovCantiere, setFiltroMovCantiere] = useState("");
  const [filtroMovTipo, setFiltroMovTipo] = useState("");
  const [filtroMovStato, setFiltroMovStato] = useState("");
  // Filtro stato dipendenti: "attivi" (default) | "non_attivi" | "tutti"
  const [filtroStatoDip, setFiltroStatoDip] = useState("attivi");
  
  // Edit movimento state
  const [editMovimento, setEditMovimento] = useState(null);
  const [editMovForm, setEditMovForm] = useState({});
  // Filtri Costi Dipendenti
  const [filtroCostiMeseDa, setFiltroCostiMeseDa] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [filtroCostiMeseA, setFiltroCostiMeseA] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [loadingCosti, setLoadingCosti] = useState(false);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [form, setForm] = useState({
    nome: "",
    cognome: "",
    attivo: true,
    appartamento_id: "",
    centro_costo_id: "",
    data_assegnazione: "",
    note: "",
    patente: false,
    residenza_appartamento_id: "",
    has_residenza: false,
  });
  
  // Per tracciare se è cambiato appartamento/centro costo in modifica
  const [showDataVariazione, setShowDataVariazione] = useState(false);
  const [dataVariazione, setDataVariazione] = useState("");
  // Memorizza lo stato attivo iniziale per capire se è passato da true → false
  const [attivoIniziale, setAttivoIniziale] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const prefix = getApiPrefix();
      const [dipRes, alloggiRes, appRes, ccRes, movRes] = await Promise.all([
        api.get(`${prefix}/dipendenti`),
        api.get(`${prefix}/alloggi-dipendenti`),
        api.get(`${prefix}/appartamenti`),
        api.get(`${prefix}/cost-centers`),
        api.get(`${prefix}/registro-movimenti`)
      ]);
      setDipendenti(dipRes.data);
      setAlloggi(alloggiRes.data);
      setAppartamenti(appRes.data);
      setCentriCosto(ccRes.data);
      setMovimenti(movRes.data);
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Carica costi dipendenti per mese
  const fetchCostiDipendenti = useCallback(async () => {
    if (!filtroCostiMeseDa) return;
    setLoadingCosti(true);
    try {
      const [anno, mese] = filtroCostiMeseDa.split('-');
      const [annoFine, meseFine] = (filtroCostiMeseA || filtroCostiMeseDa).split('-');
      const res = await api.get(`${getApiPrefix()}/costi-dipendenti?anno=${anno}&mese=${mese}&anno_fine=${annoFine}&mese_fine=${meseFine}`);
      setCostiDipendenti(res.data);
    } catch (err) {
      handleApiError(err, t("errors.loadCosts"));
    } finally {
      setLoadingCosti(false);
    }
  }, [filtroCostiMeseDa, filtroCostiMeseA, t]);

  useEffect(() => {
    if (activeTab === 'costi') {
      fetchCostiDipendenti();
    }
  }, [activeTab, fetchCostiDipendenti]);

  // Filtra movimenti
  const movimentiFiltrati = movimenti?.movimenti?.filter(m => {
    if (filtroMovDipendente && !m.dipendente.toLowerCase().includes(filtroMovDipendente.toLowerCase())) return false;
    if (filtroMovAppartamento && m.appartamento_id !== filtroMovAppartamento) return false;
    if (filtroMovCantiere && m.centro_costo_id !== filtroMovCantiere) return false;
    if (filtroMovTipo && m.tipo !== filtroMovTipo) return false;
    if (filtroMovStato === 'attivo' && !m.attivo) return false;
    if (filtroMovStato === 'concluso' && m.attivo) return false;
    return true;
  }) || [];

  const openAddDialog = () => {
    setEditingId(null);
    setEditingData(null);
    setShowDataVariazione(false);
    setDataVariazione("");
    setAttivoIniziale(true);
    setForm({
      nome: "",
      cognome: "",
      attivo: true,
      appartamento_id: "",
      centro_costo_id: "",
      data_assegnazione: new Date().toISOString().split('T')[0],
      note: "",
      patente: false,
      residenza_appartamento_id: "",
      has_residenza: false,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingId(item.id);
    setEditingData({
      appartamento_id: item.appartamento_id || "",
      centro_costo_id: item.centro_costo_id || ""
    });
    setShowDataVariazione(false);
    setDataVariazione("");
    setAttivoIniziale(item.attivo ?? true);
    setForm({
      nome: item.nome || "",
      cognome: item.cognome || "",
      attivo: item.attivo ?? true,
      appartamento_id: item.appartamento_id || "",
      centro_costo_id: item.centro_costo_id || "",
      data_assegnazione: item.data_assegnazione || "",
      data_uscita: item.data_uscita || "",
      note: item.note || "",
      patente: !!item.patente,
      residenza_appartamento_id: item.residenza_appartamento_id || "",
      has_residenza: !!item.residenza_appartamento_id,
    });
    setDialogOpen(true);
  };

  // Memorizza lo stato attivo iniziale per capire se è passato da true → false

  // Verifica se è cambiato appartamento o centro costo
  const checkAssegnazioneChanged = (field, value) => {
    if (!editingData) return;
    
    const newForm = { ...form, [field]: value };
    const appChanged = newForm.appartamento_id !== editingData.appartamento_id;
    const ccChanged = newForm.centro_costo_id !== editingData.centro_costo_id;
    
    setShowDataVariazione(appChanged || ccChanged);
    if (!appChanged && !ccChanged) {
      setDataVariazione("");
    }
  };

  const handleSubmit = async () => {
    if (!form.nome || !form.cognome) {
      handleApiError({ message: t("errors.requiredNameSurname") });
      return;
    }
    
    if (showDataVariazione && !dataVariazione) {
      handleApiError({ message: t("errors.requiredDateChange") });
      return;
    }
    
    // Validazione: se sta disattivando un dipendente, data_uscita obbligatoria
    const staDisattivando = editingId && attivoIniziale && !form.attivo;
    if (staDisattivando && !form.data_uscita) {
      handleApiError({ message: "Data uscita obbligatoria quando si disattiva un dipendente" });
      return;
    }

    setSaving(true);
    try {
      const prefix = getApiPrefix();
      
      if (editingId) {
        // Update
        const payload = {
          nome: form.nome,
          cognome: form.cognome,
          attivo: form.attivo,
          note: form.note || null,
          patente: !!form.patente,
          // Se la checkbox residenza è OFF, invia stringa vuota per rimuovere il valore
          residenza_appartamento_id: form.has_residenza ? (form.residenza_appartamento_id || "") : "",
        };
        
        // Aggiungi campi assegnazione solo se cambiati
        if (showDataVariazione) {
          payload.appartamento_id = form.appartamento_id || null;
          payload.centro_costo_id = form.centro_costo_id || null;
          payload.data_variazione = dataVariazione;
        }
        
        // data_uscita: invia se sta disattivando o se cambiata
        if (staDisattivando || (form.data_uscita && form.attivo === false)) {
          payload.data_uscita = form.data_uscita;
        }
        
        await api.put(`${prefix}/dipendenti/${editingId}`, payload);
        showSuccess(t("success.employeeUpdated"));
      } else {
        // Create
        const payload = {
          nome: form.nome,
          cognome: form.cognome,
          attivo: form.attivo,
          appartamento_id: form.appartamento_id || null,
          centro_costo_id: form.centro_costo_id || null,
          data_assegnazione: form.data_assegnazione || null,
          note: form.note || null,
          patente: !!form.patente,
          residenza_appartamento_id: form.has_residenza ? (form.residenza_appartamento_id || null) : null,
        };
        await api.post(`${prefix}/dipendenti`, payload);
        showSuccess(t("success.employeeCreated"));
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("confirm.deleteEmployee"))) return;
    setDeleting(id);
    try {
      await api.delete(`${getApiPrefix()}/dipendenti/${id}`);
      showSuccess(t("success.employeeDeleted"));
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    } finally {
      setDeleting(null);
    }
  };

  const openEditMovimento = (m) => {
    setEditMovForm({
      appartamento_id: m.appartamento_id || "",
      centro_costo_id: m.centro_costo_id || "",
      data_inizio: m.data_inizio ? m.data_inizio.split("T")[0] : "",
      data_fine: m.data_fine ? m.data_fine.split("T")[0] : "",
      note: m.note || ""
    });
    setEditMovimento(m);
  };

  const handleSaveMovimento = async () => {
    try {
      await api.put(`${getApiPrefix()}/registro-movimenti/${editMovimento.id}`, editMovForm);
      showSuccess(t("success.movementUpdated"));
      setEditMovimento(null);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.updateFailed"));
    }
  };

  // Colonne DataTable per Anagrafica
  const columns = [
    { 
      key: 'cognome_nome', 
      label: 'Dipendente',
      render: (_, row) => (
        <div>
          <span className="font-medium">{row.cognome} {row.nome}</span>
        </div>
      )
    },
    { 
      key: 'attivo', 
      label: t('common.status'),
      width: '180px',
      render: (value, row) => value ? (
        <Badge className="bg-green-100 text-green-700"><UserCheck className="h-3 w-3 mr-1" />{t('common.active')}</Badge>
      ) : (
        <div className="flex flex-col gap-1 items-start">
          <Badge variant="secondary"><UserX className="h-3 w-3 mr-1" />{t('common.inactive')}</Badge>
          {row.data_uscita && (
            <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded" data-testid={`badge-data-uscita-${row.id}`}>
              Uscito il {new Date(row.data_uscita).toLocaleDateString('it-IT')}
            </span>
          )}
        </div>
      )
    },
    { 
      key: 'appartamento_nome', 
      label: 'Appartamento',
      render: (value, row) => value ? (
        <div className="flex items-center gap-1">
          <Home className="h-4 w-4 text-blue-500" />
          <span>{value}</span>
        </div>
      ) : <span className="text-muted-foreground">-</span>
    },
    { 
      key: 'centro_costo_nome', 
      label: 'Cantiere/CDC',
      render: (value, row) => value ? (
        <div className="flex items-center gap-1">
          <HardHat className="h-4 w-4 text-orange-500" />
          <span>{row.centro_costo_codice} - {value}</span>
        </div>
      ) : <span className="text-muted-foreground">-</span>
    },
    { 
      key: 'data_assegnazione', 
      label: 'Dal',
      width: '100px',
      format: 'date'
    },
    { 
      key: 'actions', 
      label: t('common.actions'), 
      align: 'right',
      width: '100px',
      sortable: false,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => openEditDialog(row)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      )
    }
  ];

  const stats = alloggi?.statistiche || {};
  const rawStatsPerArea = alloggi?.statistiche_per_area;
  const statsPerArea = useMemo(() => rawStatsPerArea || {}, [rawStatsPerArea]);

  // Flatten elenco dipendenti dalla Vista Alloggi (un dipendente per ogni apartments.occupanti).
  // Utilizzato sia per la barra di ricerca sia per i contatori patente/residenza nelle dashboard.
  const dipendentiFlat = useMemo(() => {
    const out = [];
    (alloggi?.appartamenti || []).forEach(app => {
      (app.occupanti || []).forEach(occ => {
        out.push({
          ...occ,
          appartamento_nome: app.nome,
          appartamento_id: app.id,
          area: app.area || "Senza Area",
        });
      });
    });
    return out;
  }, [alloggi]);

  // Aggrega contatori patente / residenza per la dashboard
  const counters = useMemo(() => {
    let conPatente = 0;
    let conResidenza = 0;
    const perArea = {};
    dipendentiFlat.forEach(d => {
      const a = d.area;
      perArea[a] = perArea[a] || { conPatente: 0, conResidenza: 0 };
      if (d.patente) {
        conPatente += 1;
        perArea[a].conPatente += 1;
      }
      if (d.residenza_appartamento_id) {
        conResidenza += 1;
        perArea[a].conResidenza += 1;
      }
    });
    return { conPatente, conResidenza, perArea };
  }, [dipendentiFlat]);

  // Stato della ricerca dipendente nella Vista Alloggi
  const [searchDipendente, setSearchDipendente] = useState("");
  const [highlightedDipId, setHighlightedDipId] = useState(null);

  // Risultati della ricerca (max 8 suggerimenti)
  const searchResults = useMemo(() => {
    const q = searchDipendente.trim().toLowerCase();
    if (q.length < 2) return [];
    return dipendentiFlat
      .filter(d => `${d.cognome} ${d.nome}`.toLowerCase().includes(q) ||
                   (d.appartamento_nome || "").toLowerCase().includes(q) ||
                   (d.cantiere || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [searchDipendente, dipendentiFlat]);

  const scrollToDipendente = useCallback((dipId) => {
    setSearchDipendente("");
    setHighlightedDipId(dipId);
    // Aspetta un tick per essere sicuri che il DOM sia renderizzato
    setTimeout(() => {
      const el = document.querySelector(`[data-dip-id="${dipId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 50);
    // Pulisci highlight dopo 3 secondi
    setTimeout(() => setHighlightedDipId(null), 3000);
  }, []);
  
  // Ordina le aree alfabeticamente, mettendo "Senza Area" alla fine
  const areeOrdinate = useMemo(() => {
    return Object.keys(statsPerArea).sort((a, b) => {
      if (a === "Senza Area") return 1;
      if (b === "Senza Area") return -1;
      return a.localeCompare(b);
    });
  }, [statsPerArea]);

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
  }

  return (
    <div className="space-y-6" data-testid="alloggi-dipendenti-page">
      <div className="flex items-center gap-3">
        <Users className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("nav.employeeHousing")}</h1>
          <p className="text-muted-foreground text-sm">Gestione alloggi e assegnazioni dipendenti</p>
        </div>
      </div>

      {/* Stats Globali */}
      <div className="grid gap-4 md:grid-cols-5 lg:grid-cols-7">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Dipendenti Attivi</p>
                <p className="text-2xl font-bold tracking-tight">{stats.totale_dipendenti_attivi || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <UserCheck className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Alloggiati</p>
                <p className="text-2xl font-bold tracking-tight">{stats.dipendenti_alloggiati || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Home className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Posti Letto Totali</p>
                <p className="text-2xl font-bold tracking-tight">{stats.totale_posti_letto || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Building className="h-8 w-8 text-orange-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Posti Occupati</p>
                <p className="text-2xl font-bold tracking-tight">{stats.posti_occupati || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <UserCheck className="h-8 w-8 text-emerald-600" />
              <div>
                <p className="text-xs text-emerald-700 uppercase font-medium">Posti Liberi</p>
                <p className="text-2xl font-bold text-emerald-700">{stats.posti_liberi || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-sky-50 border-sky-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Car className="h-8 w-8 text-sky-600" />
              <div>
                <p className="text-xs text-sky-700 uppercase font-medium">Con Patente</p>
                <p className="text-2xl font-bold text-sky-700" data-testid="stat-patente">{counters.conPatente}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-violet-50 border-violet-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <IdCard className="h-8 w-8 text-violet-600" />
              <div>
                <p className="text-xs text-violet-700 uppercase font-medium">Con Residenza</p>
                <p className="text-2xl font-bold text-violet-700" data-testid="stat-residenza">{counters.conResidenza}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Stats per Area */}
      {areeOrdinate.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Globe className="h-5 w-5 text-indigo-500" />
              Riepilogo per Area
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {areeOrdinate.map((area) => {
                const areaStats = statsPerArea[area];
                const occupancyRate = areaStats.posti_letto > 0 
                  ? Math.round((areaStats.posti_occupati / areaStats.posti_letto) * 100) 
                  : 0;
                const colorClass = occupancyRate >= 90 ? 'border-red-200 bg-red-50' : 
                                   occupancyRate >= 70 ? 'border-orange-200 bg-orange-50' : 
                                   'border-green-200 bg-green-50';
                return (
                  <div key={area} className={`p-4 rounded-lg border ${colorClass}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-sm flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-indigo-500" />
                        {area}
                      </h4>
                      <Badge variant="outline" className="text-xs">
                        {occupancyRate}% occupato
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-1">
                        <Home className="h-3 w-3 text-purple-500" />
                        <span className="text-muted-foreground">Appartamenti:</span>
                        <span className="font-medium">{areaStats.appartamenti}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3 text-blue-500" />
                        <span className="text-muted-foreground">Posti:</span>
                        <span className="font-medium">{areaStats.posti_letto}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <UserCheck className="h-3 w-3 text-green-500" />
                        <span className="text-muted-foreground">Occupati:</span>
                        <span className="font-medium">{areaStats.posti_occupati}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <UserX className="h-3 w-3 text-emerald-500" />
                        <span className="text-muted-foreground">Liberi:</span>
                        <span className="font-medium text-emerald-600">{areaStats.posti_liberi}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Car className="h-3 w-3 text-sky-600" />
                        <span className="text-muted-foreground">Patente:</span>
                        <span className="font-medium text-sky-700">{counters.perArea[area]?.conPatente || 0}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <IdCard className="h-3 w-3 text-violet-600" />
                        <span className="text-muted-foreground">Residenze:</span>
                        <span className="font-medium text-violet-700">{counters.perArea[area]?.conResidenza || 0}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="alloggi" className="gap-2">
            <Home className="h-4 w-4" />
            Vista Alloggi
          </TabsTrigger>
          <TabsTrigger value="anagrafica" className="gap-2">
            <Users className="h-4 w-4" />
            Anagrafica Dipendenti
          </TabsTrigger>
          <TabsTrigger value="movimenti" className="gap-2">
            <History className="h-4 w-4" />
            Registro Movimenti
          </TabsTrigger>
          <TabsTrigger value="costi" className="gap-2">
            <Euro className="h-4 w-4" />
            Costi per Dipendente
          </TabsTrigger>
        </TabsList>

        {/* Tab Vista Alloggi */}
        <TabsContent value="alloggi" className="mt-6">
          {/* Ricerca dipendente: porta direttamente alla persona nella pagina */}
          <Card className="mb-4">
            <CardContent className="pt-4 pb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Cerca dipendente per cognome, nome, appartamento o cantiere... (min. 2 caratteri)"
                  value={searchDipendente}
                  onChange={(e) => setSearchDipendente(e.target.value)}
                  className="pl-9"
                  data-testid="search-dipendente"
                />
                {searchResults.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-72 overflow-y-auto">
                    {searchResults.map(d => (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => scrollToDipendente(d.id)}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-b-0 transition-colors"
                        data-testid={`search-result-${d.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${d.attivo ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                              {d.cognome?.[0]}{d.nome?.[0]}
                            </span>
                            <div>
                              <p className="font-medium text-sm">{d.cognome} {d.nome}</p>
                              <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                                <Home className="h-3 w-3" /> {d.appartamento_nome}
                                {d.area && <span className="text-muted-foreground/70">• {d.area}</span>}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {d.patente && <Car className="h-3 w-3 text-sky-600" title="Ha patente" />}
                            {d.residenza_appartamento_id && <IdCard className="h-3 w-3 text-violet-600" title="Ha residenza" />}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {searchDipendente.length >= 2 && searchResults.length === 0 && (
                <p className="text-xs text-muted-foreground mt-2">Nessun dipendente trovato per "{searchDipendente}"</p>
              )}
            </CardContent>
          </Card>

          {/* Raggruppa appartamenti per area */}
          {areeOrdinate.length > 0 ? (
            <div className="space-y-8">
              {areeOrdinate.map((area) => {
                const appartamentiArea = alloggi?.appartamenti?.filter(app => (app.area || "Senza Area") === area) || [];
                if (appartamentiArea.length === 0) return null;
                
                return (
                  <div key={area}>
                    <div className="flex items-center gap-2 mb-4">
                      <Globe className="h-5 w-5 text-indigo-500" />
                      <h3 className="text-lg font-semibold">{area}</h3>
                      <Badge variant="outline" className="ml-2">
                        {appartamentiArea.length} appartament{appartamentiArea.length === 1 ? 'o' : 'i'}
                      </Badge>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {appartamentiArea.map((app) => (
                        <Card key={app.id} className={`overflow-hidden ${app.posti_liberi === 0 ? 'border-red-200' : app.posti_occupati > 0 ? 'border-green-200' : 'border-dashed'}`}>
                          <CardHeader className={`pb-3 ${app.posti_liberi === 0 ? 'bg-red-50' : app.posti_occupati > 0 ? 'bg-green-50' : 'bg-gray-50'}`}>
                            <div className="flex items-start justify-between">
                              <div>
                                <CardTitle className="text-lg flex items-center gap-2">
                                  <Home className={`h-5 w-5 ${app.posti_liberi === 0 ? 'text-red-600' : app.posti_occupati > 0 ? 'text-green-600' : 'text-gray-400'}`} />
                                  {app.nome}
                                </CardTitle>
                                <CardDescription className="flex items-center gap-1 mt-1">
                                  <MapPin className="h-3 w-3" />
                                  {app.indirizzo}, {app.citta}
                                </CardDescription>
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                <Badge variant={app.posti_liberi === 0 ? "destructive" : app.posti_occupati > 0 ? "default" : "outline"}>
                                  {app.posti_occupati}/{app.posti_letto} posti
                                </Badge>
                                {app.posti_liberi > 0 && (
                                  <span className="text-xs text-emerald-600 font-medium">
                                    {app.posti_liberi} {app.posti_liberi === 1 ? 'libero' : 'liberi'}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Nuova sezione: Canone, Spese e Tipo Gestione */}
                            <div className="mt-3 pt-3 border-t border-dashed space-y-1.5">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground flex items-center gap-1">
                                  <Euro className="h-3 w-3" /> Canone:
                                </span>
                                <span className="font-semibold text-blue-700">
                                  € {(app.canone_mensile || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}
                                </span>
                              </div>
                              {app.ha_spese_condominiali && app.spese_condominiali > 0 && (
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-muted-foreground">Spese Cond.:</span>
                                  <span className="font-semibold text-orange-600">
                                    € {(app.spese_condominiali || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}
                                  </span>
                                </div>
                              )}
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">Gestione:</span>
                                <Badge variant={app.tramite_agenzia ? "secondary" : "outline"} className={app.tramite_agenzia ? "bg-purple-100 text-purple-700" : ""}>
                                  {app.tramite_agenzia ? (
                                    <>
                                      <Building className="h-3 w-3 mr-1" />
                                      {app.nome_agenzia || "Agenzia"}
                                    </>
                                  ) : (
                                    "Privato"
                                  )}
                                </Badge>
                              </div>
                            </div>
                            
                            {app.proprietario && (
                              <p className="text-xs text-muted-foreground mt-2">
                                Proprietario: {app.proprietario}
                              </p>
                            )}
                          </CardHeader>
                          <CardContent className="pt-4">
                            {app.occupanti.length > 0 ? (
                              <div className="space-y-3">
                                {app.occupanti.map((occ) => (
                                  <div
                                    key={occ.id}
                                    data-dip-id={occ.id}
                                    className={`flex items-center justify-between p-2 rounded-lg bg-white border transition-all duration-500 ${highlightedDipId === occ.id ? "ring-2 ring-blue-400 bg-blue-50 scale-[1.01]" : ""}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${occ.attivo ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                                        <span className="font-semibold text-sm">
                                          {occ.cognome?.[0]}{occ.nome?.[0]}
                                        </span>
                                      </div>
                                      <div>
                                        <p className="font-medium text-sm">{occ.cognome} {occ.nome}</p>
                                        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                                          {occ.cantiere && (
                                            <p className="text-xs text-orange-600 flex items-center gap-1">
                                              <HardHat className="h-3 w-3" />
                                              {occ.cantiere}
                                            </p>
                                          )}
                                          {occ.patente && (
                                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200" title="Possiede la patente di guida">
                                              <Car className="h-2.5 w-2.5" /> Patente
                                            </span>
                                          )}
                                          {occ.residenza_qui ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200" title="Residenza registrata in questo appartamento">
                                              <IdCard className="h-2.5 w-2.5" /> Residenza qui
                                            </span>
                                          ) : occ.residenza_appartamento_nome ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-slate-50 text-slate-600 border border-slate-200" title={`Residenza presso ${occ.residenza_appartamento_nome}`}>
                                              <IdCard className="h-2.5 w-2.5" /> Res. {occ.residenza_appartamento_nome}
                                            </span>
                                          ) : null}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Calendar className="h-3 w-3" />
                                        dal {new Date(occ.data_ingresso).toLocaleDateString('it-IT')}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-6 text-muted-foreground">
                                <Home className="h-8 w-8 mx-auto mb-2 opacity-30" />
                                <p className="text-sm">{t("apartments.noOccupants")}</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {alloggi?.appartamenti?.map((app) => (
                <Card key={app.id} className={`overflow-hidden ${app.posti_liberi === 0 ? 'border-red-200' : app.posti_occupati > 0 ? 'border-green-200' : 'border-dashed'}`}>
                  <CardHeader className={`pb-3 ${app.posti_liberi === 0 ? 'bg-red-50' : app.posti_occupati > 0 ? 'bg-green-50' : 'bg-gray-50'}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Home className={`h-5 w-5 ${app.posti_liberi === 0 ? 'text-red-600' : app.posti_occupati > 0 ? 'text-green-600' : 'text-gray-400'}`} />
                          {app.nome}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-1 mt-1">
                          <MapPin className="h-3 w-3" />
                          {app.indirizzo}, {app.citta}
                        </CardDescription>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge variant={app.posti_liberi === 0 ? "destructive" : app.posti_occupati > 0 ? "default" : "outline"}>
                          {app.posti_occupati}/{app.posti_letto} posti
                        </Badge>
                        {app.posti_liberi > 0 && (
                          <span className="text-xs text-emerald-600 font-medium">
                            {app.posti_liberi} {app.posti_liberi === 1 ? 'libero' : 'liberi'}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-dashed space-y-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Euro className="h-3 w-3" /> Canone:
                        </span>
                        <span className="font-semibold text-blue-700">
                          € {(app.canone_mensile || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}
                        </span>
                      </div>
                      {app.ha_spese_condominiali && app.spese_condominiali > 0 && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Spese Cond.:</span>
                          <span className="font-semibold text-orange-600">
                            € {(app.spese_condominiali || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Gestione:</span>
                        <Badge variant={app.tramite_agenzia ? "secondary" : "outline"} className={app.tramite_agenzia ? "bg-purple-100 text-purple-700" : ""}>
                          {app.tramite_agenzia ? (
                            <>
                              <Building className="h-3 w-3 mr-1" />
                              {app.nome_agenzia || "Agenzia"}
                            </>
                          ) : (
                            "Privato"
                          )}
                        </Badge>
                      </div>
                    </div>
                    
                    {app.proprietario && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Proprietario: {app.proprietario}
                      </p>
                    )}
                  </CardHeader>
                  <CardContent className="pt-4">
                    {app.occupanti.length > 0 ? (
                      <div className="space-y-3">
                        {app.occupanti.map((occ) => (
                          <div
                            key={occ.id}
                            data-dip-id={occ.id}
                            className={`flex items-center justify-between p-2 rounded-lg bg-white border transition-all duration-500 ${highlightedDipId === occ.id ? "ring-2 ring-blue-400 bg-blue-50 scale-[1.01]" : ""}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${occ.attivo ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                                <span className="font-semibold text-sm">
                                  {occ.cognome?.[0]}{occ.nome?.[0]}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-sm">{occ.cognome} {occ.nome}</p>
                                <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                                  {occ.cantiere && (
                                    <p className="text-xs text-orange-600 flex items-center gap-1">
                                      <HardHat className="h-3 w-3" />
                                      {occ.cantiere}
                                    </p>
                                  )}
                                  {occ.patente && (
                                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200" title="Possiede la patente di guida">
                                      <Car className="h-2.5 w-2.5" /> Patente
                                    </span>
                                  )}
                                  {occ.residenza_qui ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200" title="Residenza registrata in questo appartamento">
                                      <IdCard className="h-2.5 w-2.5" /> Residenza qui
                                    </span>
                                  ) : occ.residenza_appartamento_nome ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-slate-50 text-slate-600 border border-slate-200" title={`Residenza presso ${occ.residenza_appartamento_nome}`}>
                                      <IdCard className="h-2.5 w-2.5" /> Res. {occ.residenza_appartamento_nome}
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                dal {new Date(occ.data_ingresso).toLocaleDateString('it-IT')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-muted-foreground">
                        <Home className="h-8 w-8 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">{t("apartments.noOccupants")}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          {(!alloggi?.appartamenti || alloggi.appartamenti.length === 0) && (
            <Card className="p-8 text-center">
              <Home className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground text-sm">{t("apartments.noApartmentsFound")}</p>
              <p className="text-sm text-muted-foreground mt-1">Aggiungi appartamenti dalla sezione Appartamenti</p>
            </Card>
          )}
        </TabsContent>

        {/* Tab Anagrafica Dipendenti */}
        <TabsContent value="anagrafica" className="mt-6">
          {(() => {
            const countDipAttivi = dipendenti.filter(d => d.attivo).length;
            const countDipNonAttivi = dipendenti.filter(d => !d.attivo).length;
            const dipendentiFiltrati = filtroStatoDip === 'tutti'
              ? dipendenti
              : filtroStatoDip === 'non_attivi'
                ? dipendenti.filter(d => !d.attivo)
                : dipendenti.filter(d => d.attivo);
            return (
              <DataTable
                columns={columns}
                data={dipendentiFiltrati}
                onDelete={(ids) => ids.forEach(id => handleDelete(id))}
                idField="id"
                emptyMessage="Nessun dipendente trovato"
                toolbar={
                  <div className="flex items-center gap-2">
                    <div className="flex bg-muted rounded-lg p-0.5">
                      <Button
                        variant={filtroStatoDip === 'attivi' ? 'default' : 'ghost'}
                        size="sm" className="h-7 text-xs rounded-md"
                        onClick={() => setFiltroStatoDip('attivi')}
                        data-testid="filtro-dip-attivi"
                      >
                        Attivi ({countDipAttivi})
                      </Button>
                      <Button
                        variant={filtroStatoDip === 'non_attivi' ? 'default' : 'ghost'}
                        size="sm" className="h-7 text-xs rounded-md"
                        onClick={() => setFiltroStatoDip('non_attivi')}
                        data-testid="filtro-dip-non-attivi"
                      >
                        Non attivi ({countDipNonAttivi})
                      </Button>
                      <Button
                        variant={filtroStatoDip === 'tutti' ? 'default' : 'ghost'}
                        size="sm" className="h-7 text-xs rounded-md"
                        onClick={() => setFiltroStatoDip('tutti')}
                        data-testid="filtro-dip-tutti"
                      >
                        Tutti ({dipendenti.length})
                      </Button>
                    </div>
                    <Button onClick={openAddDialog} data-testid="add-dipendente-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Nuovo Dipendente
                    </Button>
                  </div>
                }
              />
            );
          })()}
        </TabsContent>

        {/* Tab Registro Movimenti */}
        <TabsContent value="movimenti" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />{t('common.movementHistory')}</CardTitle>
                  <CardDescription>
                    Registro di tutti i cambi di appartamento e cantiere
                  </CardDescription>
                </div>
                <div className="flex gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span>{t('apartments.title')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                    <span>{t('common.site')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                    <span>Entrambi</span>
                  </div>
                </div>
              </div>
              
              {/* Filtri */}
              <div className="mt-4 flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-xs">{t('common.employee')}</Label>
                  <Input 
                    placeholder={t("employees.searchEmployee")} 
                    value={filtroMovDipendente}
                    onChange={(e) => setFiltroMovDipendente(e.target.value)}
                    className="h-9"
                  />
                </div>
                <div className="w-[180px]">
                  <Label className="text-xs">{t('apartments.title')}</Label>
                  <SearchableSelect
                    options={[
                      { value: "_all", label: "Tutti" },
                      ...appartamenti.map(a => ({ value: a.id, label: a.nome || a.indirizzo }))
                    ]}
                    value={filtroMovAppartamento || "_all"}
                    onValueChange={(v) => setFiltroMovAppartamento(v === "_all" ? "" : v)}
                    placeholder={t("common.all")}
                    searchPlaceholder={t("employees.searchApartment")}
                    testId="filter-mov-appartamento"
                  />
                </div>
                <div className="w-[180px]">
                  <Label className="text-xs">{t('common.site')}</Label>
                  <SearchableSelect
                    options={[
                      { value: "_all", label: "Tutti" },
                      ...centriCosto.map(cc => ({ value: cc.id, label: `${cc.codice} - ${cc.nome}` }))
                    ]}
                    value={filtroMovCantiere || "_all"}
                    onValueChange={(v) => setFiltroMovCantiere(v === "_all" ? "" : v)}
                    placeholder={t("common.all")}
                    searchPlaceholder={t("common.searchPlaceholder")}
                    testId="filter-mov-cantiere"
                  />
                </div>
                <div className="w-[130px]">
                  <Label className="text-xs">{t("common.type")}</Label>
                  <Select value={filtroMovTipo || "_all"} onValueChange={(v) => setFiltroMovTipo(v === "_all" ? "" : v)}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder={t("common.all")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">{t('common.all')}</SelectItem>
                      <SelectItem value="appartamento">{t('apartments.title')}</SelectItem>
                      <SelectItem value="cantiere">{t('common.site')}</SelectItem>
                      <SelectItem value="entrambi">Entrambi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-[120px]">
                  <Label className="text-xs">{t("common.status")}</Label>
                  <Select value={filtroMovStato || "_all"} onValueChange={(v) => setFiltroMovStato(v === "_all" ? "" : v)}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder={t("common.all")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">{t('common.all')}</SelectItem>
                      <SelectItem value="attivo">{t('common.active')}</SelectItem>
                      <SelectItem value="concluso">{t('common.completed')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(filtroMovDipendente || filtroMovAppartamento || filtroMovCantiere || filtroMovTipo || filtroMovStato) && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setFiltroMovDipendente("");
                      setFiltroMovAppartamento("");
                      setFiltroMovCantiere("");
                      setFiltroMovTipo("");
                      setFiltroMovStato("");
                    }}
                    className="h-9"
                  >
                    <X className="h-4 w-4 mr-1" />{t('common.reset')}</Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">{t('common.employee')}</TableHead>
                      <TableHead className="w-[120px]">{t('common.startDate')}</TableHead>
                      <TableHead className="w-[120px]">{t('common.endDate')}</TableHead>
                      <TableHead>{t('apartments.title')}</TableHead>
                      <TableHead>{t('common.site')}</TableHead>
                      <TableHead className="w-[100px]">{t("common.status")}</TableHead>
                      <TableHead className="w-[60px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {movimentiFiltrati.map((m) => {
                      // Colori per tipo di movimento
                      const bgColor = m.tipo === 'appartamento' 
                        ? 'bg-blue-50 hover:bg-blue-100' 
                        : m.tipo === 'cantiere' 
                        ? 'bg-orange-50 hover:bg-orange-100'
                        : m.tipo === 'entrambi'
                        ? 'bg-purple-50 hover:bg-purple-100'
                        : 'bg-gray-50 hover:bg-gray-100';
                      
                      const borderColor = m.tipo === 'appartamento'
                        ? 'border-l-4 border-l-blue-500'
                        : m.tipo === 'cantiere'
                        ? 'border-l-4 border-l-orange-500'
                        : m.tipo === 'entrambi'
                        ? 'border-l-4 border-l-purple-500'
                        : 'border-l-4 border-l-gray-300';
                      
                      return (
                        <TableRow key={m.id} className={`${bgColor} ${borderColor}`}>
                          <TableCell>
                            <div className="font-medium">{m.dipendente}</div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-sm">
                              <Calendar className="h-3 w-3 text-muted-foreground" />
                              {m.data_inizio ? new Date(m.data_inizio).toLocaleDateString('it-IT') : '-'}
                            </div>
                          </TableCell>
                          <TableCell>
                            {m.data_fine ? (
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                {new Date(m.data_fine).toLocaleDateString('it-IT')}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {m.appartamento ? (
                              <div className="flex items-center gap-2">
                                <Home className="h-4 w-4 text-blue-500" />
                                <div>
                                  <div className="text-sm font-medium">{m.appartamento}</div>
                                  {m.appartamento_indirizzo && (
                                    <div className="text-xs text-muted-foreground">{m.appartamento_indirizzo}</div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {m.centro_costo ? (
                              <div className="flex items-center gap-2">
                                <HardHat className="h-4 w-4 text-orange-500" />
                                <span className="text-sm">{m.centro_costo}</span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {m.attivo ? (
                              <Badge className="bg-green-100 text-green-700">{t('common.active')}</Badge>
                            ) : (
                              <Badge variant="secondary">{t('common.completed')}</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => openEditMovimento(m)} title={t("common.edit")}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {movimentiFiltrati.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          <History className="h-8 w-8 mx-auto mb-2 opacity-30" />
                          {movimenti?.movimenti?.length > 0 ? 'Nessun movimento corrisponde ai filtri' : 'Nessun movimento registrato'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              
              {/* Contatore risultati filtrati */}
              {(filtroMovDipendente || filtroMovAppartamento || filtroMovCantiere || filtroMovTipo || filtroMovStato) && (
                <p className="text-sm text-muted-foreground mt-2">
                  Mostrati {movimentiFiltrati.length} di {movimenti?.movimenti?.length || 0} movimenti
                </p>
              )}
              
              {/* Statistiche movimenti */}
              {movimenti?.statistiche && (
                <div className="mt-4 grid grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold tracking-tight">{movimenti.statistiche.totale_movimenti}</p>
                    <p className="text-xs text-muted-foreground uppercase">{t("employees.totalMovements")}</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-700">{movimenti.statistiche.movimenti_attivi}</p>
                    <p className="text-xs text-green-600 uppercase">{t('common.activePlural')}</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-700">{movimenti.statistiche.cambi_appartamento}</p>
                    <p className="text-xs text-blue-600 uppercase">Cambi Appartamento</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <p className="text-2xl font-bold text-orange-700">{movimenti.statistiche.cambi_cantiere}</p>
                    <p className="text-xs text-orange-600 uppercase">Cambi Cantiere</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Costi per Dipendente */}
        <TabsContent value="costi" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Euro className="h-5 w-5" />
                    Costi Mensili per Dipendente
                  </CardTitle>
                  <CardDescription>
                    Canone + Spese Condominiali + Bollette proporzionati ai giorni di permanenza
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs whitespace-nowrap">Da:</Label>
                    <Input 
                      type="month" 
                      value={filtroCostiMeseDa}
                      onChange={(e) => {
                        setFiltroCostiMeseDa(e.target.value);
                        if (e.target.value > filtroCostiMeseA) setFiltroCostiMeseA(e.target.value);
                      }}
                      className="w-[155px] h-9"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs whitespace-nowrap">A:</Label>
                    <Input 
                      type="month" 
                      value={filtroCostiMeseA}
                      onChange={(e) => setFiltroCostiMeseA(e.target.value)}
                      min={filtroCostiMeseDa}
                      className="w-[155px] h-9"
                    />
                  </div>
                  <Button size="sm" onClick={() => {
                    const y = new Date().getFullYear();
                    setFiltroCostiMeseDa(`${y}-01`);
                    setFiltroCostiMeseA(`${y}-12`);
                  }} variant="outline" className="h-9 text-xs">
                    Anno intero
                  </Button>
                  <Button onClick={fetchCostiDipendenti} disabled={loadingCosti} className="h-9">
                    {loadingCosti ? <Loader2 className="h-4 w-4 animate-spin" /> : "Calcola"}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingCosti ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : costiDipendenti ? (
                <>
                  {/* Statistiche */}
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-700">€ {costiDipendenti.statistiche?.totale_canoni?.toLocaleString('it-IT', {minimumFractionDigits: 2})}</p>
                      <p className="text-xs text-blue-600 uppercase">{t("employees.totalRents")}</p>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <p className="text-2xl font-bold text-orange-700">€ {costiDipendenti.statistiche?.totale_spese_cond?.toLocaleString('it-IT', {minimumFractionDigits: 2})}</p>
                      <p className="text-xs text-orange-600 uppercase">{t('common.condominiumFees')}</p>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <p className="text-2xl font-bold text-purple-700">€ {costiDipendenti.statistiche?.totale_bollette?.toLocaleString('it-IT', {minimumFractionDigits: 2})}</p>
                      <p className="text-xs text-purple-600 uppercase">Bollette</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg border-2 border-green-200">
                      <p className="text-2xl font-bold text-green-700">€ {costiDipendenti.totale_generale?.toLocaleString('it-IT', {minimumFractionDigits: 2})}</p>
                      <p className="text-xs text-green-600 uppercase font-medium">{t("employees.totalGeneral")}</p>
                    </div>
                  </div>
                  
                  {/* Tabella costi */}
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('common.employee')}</TableHead>
                          <TableHead className="text-right">{t('apartments.canone')}</TableHead>
                          <TableHead className="text-right">Spese Cond.</TableHead>
                          <TableHead className="text-right">Bollette</TableHead>
                          <TableHead className="text-right">{t("common.total")}</TableHead>
                          <TableHead>Dettaglio</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {costiDipendenti.dipendenti?.map((d) => (
                          <TableRow key={d.dipendente_id}>
                            <TableCell>
                              <div className="font-medium">{d.dipendente}</div>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-blue-700">€ {d.costo_canone.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-orange-700">€ {d.costo_spese_condominiali.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-purple-700">€ {d.costo_bollette.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="font-bold text-green-700">€ {d.totale.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
                            </TableCell>
                            <TableCell>
                              {d.dettagli?.length > 0 && (
                                <div className="text-xs text-muted-foreground space-y-1">
                                  {d.dettagli.map((det, i) => (
                                    <div key={`det-${det.appartamento}-${i}`} className="flex items-center gap-2">
                                      <Home className="h-3 w-3" />
                                      <span>{det.appartamento}</span>
                                      <Badge variant="outline" className="text-[10px]">{det.giorni} gg</Badge>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        {(!costiDipendenti.dipendenti || costiDipendenti.dipendenti.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                              <Euro className="h-8 w-8 mx-auto mb-2 opacity-30" />
                              Nessun costo per il periodo selezionato
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  
                  <p className="text-xs text-muted-foreground mt-4">
                    * Periodo: {costiDipendenti.periodo} ({costiDipendenti.giorni_mese} giorni{costiDipendenti.num_mesi > 1 ? `, ${costiDipendenti.num_mesi} mesi` : ''}) - Costi proporzionali ai giorni di permanenza
                  </p>
                </>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Euro className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>{t("employees.selectMonthAndCalculate")}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <DipendenteFormDialog
        open={dialogOpen} onClose={() => { setDialogOpen(false); setEditingId(null); }}
        editingId={editingId} form={form} setForm={setForm}
        onSubmit={handleSubmit} saving={saving}
        appartamenti={appartamenti} centriCosto={centriCosto}
        checkAssegnazioneChanged={checkAssegnazioneChanged}
        showDataVariazione={showDataVariazione}
        dataVariazione={dataVariazione} setDataVariazione={setDataVariazione}
        attivoIniziale={attivoIniziale}
        t={t}
      />

      <EditMovimentoDialog
        open={!!editMovimento} onClose={() => setEditMovimento(null)}
        editMovimento={editMovimento} editMovForm={editMovForm} setEditMovForm={setEditMovForm}
        onSave={handleSaveMovimento} appartamenti={appartamenti} centriCosto={centriCosto} t={t}
      />
    </div>
  );
}
